import {
  GWP_WITHOUT_CODE_DUMMY_HASH,
  LOCAL_STORAGE_KEY_GWP_COLLECTION_DATA,
  LOCAL_STORAGE_KEY_GWP_THEME_DATA,
  LOCAL_STORAGE_KEY_GWP_USER_INPUT_CODES,
  PUB_SUB_EVENTS,
  cartDebug,
  getLocalStorage,
  publish
} from "./chunk--chunk-CBS2FRJT.vbt.js";

// src/js/helpers/gwpCodeUtils.js
function getMatchingThemeGwpProducts(codeObj, idOrHash = "hash") {
  const { gwpId, codeHash } = codeObj;
  const themeGwpProducts = getThemeGwpData();
  if (!themeGwpProducts || !Array.isArray(themeGwpProducts)) {
    throw new Error("Theme GWP data not found or invalid");
  }
  const matchingThemeGwpProducts = themeGwpProducts.filter((val) => {
    if (idOrHash === "id") {
      return val.gwpId === gwpId;
    } else {
      return val.codeHash === codeHash;
    }
  });
  return matchingThemeGwpProducts;
}
function getMatchingUserInputCode(codeObj, idOrHash = "hash") {
  const { gwpId, codeHash } = codeObj;
  const userInputCodes = getUserInputCodes();
  if (!userInputCodes || !Array.isArray(userInputCodes)) {
    throw new Error("User input codes not found or invalid");
  }
  const matchingUserInputCode = userInputCodes.find((val) => {
    if (idOrHash === "id") {
      return val.gwpId === gwpId;
    } else {
      return val.codeHash === codeHash;
    }
  });
  return matchingUserInputCode;
}
function testCartMeetsProductPurchaseRequirement(requiredProductPurchases, cartItemVariantIds, requiredPurchasesMustPurchaseAll) {
  if (requiredProductPurchases.length === 0) {
    return {
      doesMeetProductPurchaseRequirement: true,
      productsNeededToMeetRequirement: []
    };
  }
  let missingProducts = [];
  for (let product of requiredProductPurchases) {
    if (!cartItemVariantIds.includes(product.variantId)) {
      missingProducts.push(product);
    }
  }
  let doesMeetProductPurchaseRequirement = false;
  if (requiredPurchasesMustPurchaseAll) {
    doesMeetProductPurchaseRequirement = missingProducts.length === 0;
  } else {
    doesMeetProductPurchaseRequirement = missingProducts.length < requiredProductPurchases.length;
  }
  return {
    doesMeetProductPurchaseRequirement,
    productsNeededToMeetRequirement: missingProducts
  };
}
function testCartMeetsCollectionPurchaseRequirement(requiredCollectionPurchases, cartItemVariantIds, requiredPurchasesMustPurchaseAll) {
  if (requiredCollectionPurchases.length === 0) {
    return {
      doesMeetCollectionPurchaseRequirement: true,
      collectionsNeededToMeetRequirement: []
    };
  }
  const muradGwpCollectionData = getLocalStorage(
    LOCAL_STORAGE_KEY_GWP_COLLECTION_DATA
  );
  let missingCollections = [];
  for (let collection of requiredCollectionPurchases) {
    const collectionProducts = muradGwpCollectionData?.[collection.handle]?.products ?? [];
    const cartIncludesSomeCollectionItems = collectionProducts.some(
      (product) => cartItemVariantIds.includes(product.variantId)
    );
    if (!cartIncludesSomeCollectionItems) {
      missingCollections.push(collection);
    }
  }
  let doesMeetCollectionPurchaseRequirement = false;
  if (requiredPurchasesMustPurchaseAll) {
    doesMeetCollectionPurchaseRequirement = missingCollections.length === 0;
  } else {
    doesMeetCollectionPurchaseRequirement = missingCollections.length < requiredCollectionPurchases.length;
  }
  return {
    doesMeetCollectionPurchaseRequirement,
    collectionsNeededToMeetRequirement: missingCollections
  };
}
function testCartMeetsPriceThreshold(threshold, cartTotalPrice = 0) {
  cartDebug("testCartMeetsPriceThreshold", { threshold, cartTotalPrice });
  let centsNeededToReachThreshold = parseInt(threshold) - cartTotalPrice;
  let centsNeededToReachThresholdString = centsNeededToReachThreshold.toString();
  let dollarsNeededToReachThresholdString = centsNeededToReachThresholdString.slice(0, -2) + "." + centsNeededToReachThresholdString.slice(-2);
  if (centsNeededToReachThreshold > 0) {
    return {
      doesMeetThreshold: false,
      dollarsNeededToReachThresholdString
    };
  } else {
    return { doesMeetThreshold: true, dollarsNeededToReachThresholdString };
  }
}
var shapeShopifyAddToCartPayload = (item) => ({
  id: item.variantId,
  quantity: 1,
  properties: {
    // Note, in case in future you use Shopify Discounts to make a paid item free: under current implementation, "_gwp:true" will prevent users from removing item (by modifying UI), even if GWP code is removed and item now has a cost;
    _gwp: true,
    _gwp_id: item.gwpId,
    _gwp_code_value: item.codeValue,
    _gwp_code_hash: item.codeHash,
    _gwp_threshold: parseInt(item.threshold) || null,
    // _gwp_code_message: item.codeCartMessage,
    // "Size" property allows displaying size in cart; if not included, Shopify will see different properties and will treat as a different line item, even if same variantId;
    Size: item.size
  }
});
function getUserInputCodeDataForGwpItem(muradGwpUserInputCodes, codeHash) {
  let codeAddedForItem = null;
  if (codeHash === GWP_WITHOUT_CODE_DUMMY_HASH) {
    codeAddedForItem = {
      codeHash: GWP_WITHOUT_CODE_DUMMY_HASH,
      codeValue: ""
      // codeCartMessage: "Free with purchase",
    };
  } else {
    codeAddedForItem = muradGwpUserInputCodes.find(
      (codeVal) => codeVal.codeHash === codeHash && (codeVal.status === "validApplied" || codeVal.status === "validMissingProducts" || codeVal.status === "validMissingCollectionProducts" || codeVal.status === "validBelowThreshold")
    );
  }
  return codeAddedForItem;
}
function checkGwpItemRequirements(themeGwpCode, cartTotalPrice, cartItemVariantIds) {
  let { doesMeetProductPurchaseRequirement, productsNeededToMeetRequirement } = testCartMeetsProductPurchaseRequirement(
    themeGwpCode.requiredProductPurchases,
    cartItemVariantIds,
    themeGwpCode.requiredPurchasesMustPurchaseAll
  );
  let {
    doesMeetCollectionPurchaseRequirement,
    collectionsNeededToMeetRequirement
  } = testCartMeetsCollectionPurchaseRequirement(
    themeGwpCode.requiredCollectionPurchases,
    cartItemVariantIds,
    themeGwpCode.requiredPurchasesMustPurchaseAll
  );
  let { doesMeetThreshold, dollarsNeededToReachThresholdString } = testCartMeetsPriceThreshold(themeGwpCode.threshold, cartTotalPrice);
  const requirementsSatisfied = doesMeetProductPurchaseRequirement && doesMeetCollectionPurchaseRequirement && doesMeetThreshold;
  cartDebug({
    giftVariantId: themeGwpCode.variantId,
    doesMeetProductPurchaseRequirement,
    doesMeetCollectionPurchaseRequirement,
    doesMeetThreshold
  });
  return requirementsSatisfied;
}
function isLineItemGwp(cartItemWithGwpProps) {
  const { _gwp } = cartItemWithGwpProps.properties;
  if (_gwp === true) {
    return true;
  }
  return false;
}
function getThemeGwpData() {
  return getLocalStorage(LOCAL_STORAGE_KEY_GWP_THEME_DATA) ?? [];
}
function getUserInputCodes() {
  return getLocalStorage(LOCAL_STORAGE_KEY_GWP_USER_INPUT_CODES) ?? [];
}
function gwpIsThresholdReplaced({
  themeGwpCode,
  cartTotalPrice,
  gwpThemeData
}) {
  if (themeGwpCode.codeHash !== GWP_WITHOUT_CODE_DUMMY_HASH || themeGwpCode.replaceWithHigherThresholdGwp !== true) {
    return false;
  }
  const higherThresholdNoCodeGwps = gwpThemeData.filter((gwpCode) => {
    return gwpCode.codeHash === GWP_WITHOUT_CODE_DUMMY_HASH && parseInt(gwpCode.threshold) > parseInt(themeGwpCode.threshold);
  });
  for (let higherThresholdNoCodeGwp of higherThresholdNoCodeGwps) {
    const { doesMeetThreshold } = testCartMeetsPriceThreshold(
      higherThresholdNoCodeGwp.threshold,
      cartTotalPrice
    );
    if (doesMeetThreshold) {
      cartDebug(
        `GWP w/o code with threshold ${themeGwpCode.threshold} is replaced by GWP w/o code with higher threshold ${higherThresholdNoCodeGwp.threshold}`
      );
      return true;
    }
  }
  return false;
}
function checkGwpValidity({
  themeGwpCode,
  cartTotalPrice,
  cartItemVariantIds,
  gwpThemeData
}) {
  const muradGwpUserInputCodes = getUserInputCodes();
  const codeAddedForItem = getUserInputCodeDataForGwpItem(
    muradGwpUserInputCodes,
    themeGwpCode.codeHash
  );
  const gwpItemRequirementsAreSatisfied = checkGwpItemRequirements(
    themeGwpCode,
    cartTotalPrice,
    cartItemVariantIds
  );
  const isThresholdReplaced = gwpIsThresholdReplaced({
    themeGwpCode,
    cartTotalPrice,
    gwpThemeData
  });
  cartDebug({ isThresholdReplaced });
  if (codeAddedForItem && gwpItemRequirementsAreSatisfied && !isThresholdReplaced) {
    return {
      isValid: true,
      cartRequirementsAreMet: gwpItemRequirementsAreSatisfied,
      codeAddedForItem
    };
  }
  return {
    isValid: false,
    cartRequirementsAreMet: gwpItemRequirementsAreSatisfied,
    codeAddedForItem
  };
}
function getGwpToRemove(cartData) {
  const { cartTotalPrice, cartItemVariantIds, cartItemsWithGwpProps } = cartData;
  const gwpToRemove = [];
  for (let cartItem of cartItemsWithGwpProps) {
    if (isLineItemGwp(cartItem)) {
      cartDebug("Is gwp:", cartItem);
      const { _gwp_id, _gwp_code_hash, _gwp_code_value } = cartItem.properties;
      const matchingThemeGwpProducts = getMatchingThemeGwpProducts(
        { gwpId: _gwp_id, codeHash: _gwp_code_hash },
        "id"
      );
      cartDebug({ matchingThemeGwpProducts });
      const gwpCodeInTheme = matchingThemeGwpProducts[0];
      if (gwpCodeInTheme) {
        cartDebug("Is in theme:", cartItem);
        const gwpValidity = checkGwpValidity({
          themeGwpCode: gwpCodeInTheme,
          cartTotalPrice,
          cartItemVariantIds,
          gwpThemeData: getThemeGwpData()
        });
        const { isValid } = gwpValidity;
        if (!isValid) {
          cartDebug(
            `Gift assoc. with ${_gwp_code_value} in cart, but requirements are no longer met; removing gift;`
          );
          gwpToRemove.push(cartItem);
        } else {
          cartDebug("Is valid:", cartItem);
        }
      } else {
        cartDebug(
          `Gift assoc. with ${_gwp_code_value} in cart, but code no longer in GWP settings; removing gift;`
        );
        gwpToRemove.push(cartItem);
      }
    }
  }
  return gwpToRemove;
}
function getGwpToAdd(cartData) {
  const { cartTotalPrice, cartItemVariantIds } = cartData;
  const gwpToAdd = [];
  const gwpThemeData = getThemeGwpData();
  for (let themeGwpCode of gwpThemeData) {
    const gwpValidity = checkGwpValidity({
      themeGwpCode,
      cartTotalPrice,
      cartItemVariantIds,
      gwpThemeData
    });
    const { isValid, codeAddedForItem } = gwpValidity;
    if (isValid && // Murad rules limit quantity of gift item to one; if gift is already in cart, don't add it again;
    !cartItemVariantIds.includes(themeGwpCode.variantId)) {
      gwpToAdd.push({ ...themeGwpCode, ...codeAddedForItem });
    }
  }
  return gwpToAdd;
}
function getGwpItems(cartData) {
  const giftsToAdd = getGwpToAdd(cartData).map(shapeShopifyAddToCartPayload);
  const giftsToRemove = getGwpToRemove(cartData).reduce((prev, curr) => {
    return {
      ...prev,
      [curr.variant_id]: 0
    };
  }, {});
  return {
    giftsToAdd,
    giftsToRemove,
    doAddGifts: giftsToAdd.length > 0,
    doRemoveGifts: Object.keys(giftsToRemove).length > 0
  };
}
function addRemoveGwpItems(cartData) {
  const { doAddGifts, doRemoveGifts, giftsToAdd, giftsToRemove } = getGwpItems(cartData);
  if (doRemoveGifts) {
    publish(PUB_SUB_EVENTS.cartUpdateRequest, {
      variantQuantityMap: giftsToRemove
    });
  }
  if (doAddGifts) {
    publish(PUB_SUB_EVENTS.cartAddRequest, {
      variantArray: giftsToAdd
    });
  }
  return { doAddGifts, doRemoveGifts, giftsToAdd, giftsToRemove };
}

export {
  getMatchingThemeGwpProducts,
  getMatchingUserInputCode,
  testCartMeetsProductPurchaseRequirement,
  testCartMeetsCollectionPurchaseRequirement,
  testCartMeetsPriceThreshold,
  addRemoveGwpItems
};
